import { useState, useEffect, useCallback, useRef } from 'react';
import './DispatchHistory.css';
import { dispatchAPI } from '../../../config/dispatch-api';
import { toast } from 'react-toastify';
import { Pencil, Trash2, X, ArrowLeft } from 'lucide-react';
import DispatchEditModal from './DispatchEditModal/DispatchEditModal';
import { formatDateOnly } from '../../../utils/utils';

const DispatchHistory = ({ onPageChange }) => {
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [storeCoordinates, setStoreCoordinates] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    filterType: 'today',
    startDate: '',
    slot: 'ALL'
  });

  // Pagination
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    hasNext: false,
    hasPrev: false
  });

  // Edit Modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState(null);

  // Delete Modal
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [routeToDelete, setRouteToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const hasInitializedRef = useRef(false);

  // Sort time slots helper
  const sortTimeSlots = (slots) => {
    return [...slots].sort((a, b) => {
      const parseTime = (timeStr) => {
        if (!timeStr) return 0;
        const startTime = timeStr.split('-')[0].trim();
        const [time, period] = startTime.split(' ');
        let [hours, minutes] = time.split(':');

        hours = parseInt(hours);
        if (period === 'PM' && hours !== 12) hours += 12;
        if (period === 'AM' && hours === 12) hours = 0;

        return hours * 60 + (parseInt(minutes) || 0);
      };

      return parseTime(a) - parseTime(b);
    });
  };

  const fetchDispatchHistory = useCallback(async (page = 1, filterOverrides = {}) => {
    const filterType = filterOverrides.filterType ?? filters.filterType;
    const startDate = filterOverrides.startDate ?? filters.startDate;
    const slot = filterOverrides.slot ?? filters.slot;
    try {
      setLoading(true);
      setError(null);

      const response = await dispatchAPI.getDispatchHistory(page, 20, filterType, startDate, slot);

      if (response.success) {
        setRoutes(response.data.routes || []);
        setPagination(response.data.pagination || {});
        if (response.data.storeCoordinates) {
          setStoreCoordinates(response.data.storeCoordinates);
        }
      } else {
        setError(response.msg || 'Failed to fetch dispatch history');
        toast.error('Failed to load dispatch history');
      }
    } catch (err) {
      console.error('Error fetching dispatch history:', err);
      setError('Failed to fetch dispatch history');
      toast.error('Failed to load dispatch history');
    } finally {
      setLoading(false);
    }
  }, [filters.filterType, filters.startDate, filters.slot]);

  // Initial load + refresh slots when date filter changes
  useEffect(() => {
    let isActive = true;

    const load = async () => {
      if (filters.filterType === 'custom' && !filters.startDate) {
        return;
      }

      const isInitialLoad = !hasInitializedRef.current;
      const slotsPromise = dispatchAPI.getDispatchHistorySlots(filters.filterType, filters.startDate);

      try {
        if (isInitialLoad) {
          setLoading(true);
          setError(null);

          const [detailsResponse, slotsResponse, historyResponse] = await Promise.all([
            dispatchAPI.getDispatchDetails(),
            slotsPromise,
            dispatchAPI.getDispatchHistory(1, 20, filters.filterType, filters.startDate, filters.slot),
          ]);

          if (!isActive) return;

          if (detailsResponse.success && detailsResponse.data) {
            if (detailsResponse.data.drivers) {
              setDrivers(detailsResponse.data.drivers);
            }
            if (detailsResponse.data.storeCoordinates) {
              setStoreCoordinates(detailsResponse.data.storeCoordinates);
            }
          }

          if (slotsResponse.success) {
            setAvailableSlots(sortTimeSlots(slotsResponse.data || []));
          }

          if (historyResponse.success) {
            setRoutes(historyResponse.data.routes || []);
            setPagination(historyResponse.data.pagination || {});
            if (historyResponse.data.storeCoordinates) {
              setStoreCoordinates(historyResponse.data.storeCoordinates);
            }
          } else {
            setError(historyResponse.msg || 'Failed to fetch dispatch history');
            toast.error('Failed to load dispatch history');
          }

          hasInitializedRef.current = true;
        } else {
          const slotsResponse = await slotsPromise;
          if (!isActive) return;

          if (slotsResponse.success) {
            setAvailableSlots(sortTimeSlots(slotsResponse.data || []));
          }
        }
      } catch (err) {
        if (!isActive) return;
        console.error('Error loading dispatch history page:', err);
        if (isInitialLoad) {
          setError('Failed to fetch dispatch history');
          toast.error('Failed to load dispatch history');
        }
      } finally {
        if (isActive && isInitialLoad) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      isActive = false;
    };
  }, [filters.filterType, filters.startDate]);

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => {
      const next = { ...prev, [filterName]: value };
      if (filterName === 'filterType' || filterName === 'startDate') {
        next.slot = 'ALL';
      }
      return next;
    });
  };

  // Apply filters
  const applyFilters = () => {
    if (filters.filterType === 'custom' && !filters.startDate) {
      toast.error('Please select a date');
      return;
    }
    fetchDispatchHistory(1);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchDispatchHistory(newPage);
    }
  };

  // Get driver color by name
  const getDriverColor = (driverName) => {
    const driver = drivers.find(d => d.name === driverName);
    return driver?.color || '#FE8403';
  };

  // Count total orders in a route
  const countOrders = (route) => {
    if (!route.plans || !Array.isArray(route.plans)) return 0;
    return route.plans.reduce((total, plan) => {
      return total + (plan.stops?.length || 0);
    }, 0);
  };

  // Get drivers from a route
  const getRouteDrivers = (route) => {
    if (!route.plans || !Array.isArray(route.plans)) return [];
    return route.plans.map(plan => ({
      name: plan.driver,
      color: plan.color || getDriverColor(plan.driver)
    })).filter(d => d.name);
  };

  // Handle edit click
  const handleEditClick = (route) => {
    setSelectedRoute(route);
    setShowEditModal(true);
  };

  // Handle modal close
  const handleModalClose = () => {
    setShowEditModal(false);
    setSelectedRoute(null);
  };

  // Handle save from modal
  const handleSaveRoute = async (updatedRoute) => {
    try {
      const response = await dispatchAPI.updateRouteInHistory(
        updatedRoute._id || updatedRoute.id,
        updatedRoute.plans,
        updatedRoute.slot,
        updatedRoute.date,
        updatedRoute.slotId
      );

      if (response.success) {
        toast.success('Route updated successfully');
        fetchDispatchHistory(pagination.currentPage);
        return true;
      } else {
        toast.error(response.msg || 'Failed to update route');
        return false;
      }
    } catch (err) {
      console.error('Error saving route:', err);
      toast.error('Failed to save route');
      return false;
    }
  };

  // Handle delete click
  const handleDeleteClick = (routeId) => {
    setRouteToDelete(routeId);
    setShowDeleteDialog(true);
  };

  // Confirm delete
  const confirmDelete = async () => {
    if (!routeToDelete) return;

    try {
      setDeleteLoading(true);
      const response = await dispatchAPI.deleteRouteHistory(routeToDelete);

      if (response.success) {
        toast.success('Route deleted successfully!');
        fetchDispatchHistory(pagination.currentPage);
        setShowDeleteDialog(false);
        setRouteToDelete(null);
      } else {
        toast.error(response.msg || 'Failed to delete route');
      }
    } catch (error) {
      console.error('Error deleting route:', error);
      toast.error('Failed to delete route');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Cancel delete
  const cancelDelete = () => {
    setShowDeleteDialog(false);
    setRouteToDelete(null);
  };

  return (
    <div className="dispatch-history-page">
      <div className="dispatch-history-header">
        <div className="dispatch-history-title">
          <h1>Dispatch History</h1>
          <p>View and manage all dispatch plans with driver assignments</p>
        </div>
        {onPageChange && (
          <button
            className="dispatch-planner-back-btn"
            onClick={() => onPageChange('dispatch-planner')}
          >
            <ArrowLeft size={16} />
            Back to Planner
          </button>
        )}
      </div>

      {/* Filters Section */}
      <div className="filter-section">
        <div className="filter-form">
          <div className="filter-group">
            <label>Filter Type</label>
            <select
              value={filters.filterType}
              onChange={(e) => handleFilterChange('filterType', e.target.value)}
              className="filter-select"
            >
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="custom">Custom Date</option>
            </select>
          </div>

          {filters.filterType === 'custom' && (
            <div className="filter-group">
              <label>Select Date</label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                className="filter-input"
              />
            </div>
          )}

          <div className="filter-group">
            <label>Time Slot</label>
            <select
              value={filters.slot}
              onChange={(e) => handleFilterChange('slot', e.target.value)}
              className="filter-select"
            >
              <option value="ALL">All Timeslots</option>
              {availableSlots.map((slot, index) => (
                <option key={index} value={slot}>{slot}</option>
              ))}
            </select>
          </div>

          <button onClick={applyFilters} className="apply-filters-button">
            Apply Filters
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="dispatch-history-table-container">
        {loading ? (
          <div className="dispatch-history-loading-state">
            <div className="dispatch-history-loading-spinner"></div>
            <p>Loading dispatch history...</p>
          </div>
        ) : error ? (
          <div className="dispatch-history-error-state">
            <p>{error}</p>
          </div>
        ) : routes.length === 0 ? (
          <div className="dispatch-history-empty-state">
            <p>No dispatch history found</p>
          </div>
        ) : (
          <>
            <div className="dispatch-history-table-wrapper">
              <table className="dispatch-history-table">
                <thead>
                  <tr>
                    <th>Drivers</th>
                    <th>Slot</th>
                    <th>Date</th>
                    <th>Orders</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {routes.map((route) => (
                    <tr key={route._id || route.id}>
                      <td>
                        <div className="dispatch-history-driver-tags">
                          {getRouteDrivers(route).map((driver, idx) => (
                            <span
                              key={idx}
                              className="dispatch-history-driver-tag"
                              style={{ backgroundColor: driver.color }}
                            >
                              {driver.name}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td>
                        <div className="dispatch-history-slot-display">
                          <span className="dispatch-history-slot-time">{route.slot || '-'}</span>
                        </div>
                      </td>
                      <td>
                        <span className="dispatch-history-slot-date">{formatDateOnly(route.date)}</span>
                      </td>
                      <td>
                        <span className="dispatch-history-orders-count">{countOrders(route)} orders</span>
                      </td>
                      <td>
                        <div className="dispatch-history-action-buttons">
                          <button
                            className="dispatch-history-edit-btn"
                            onClick={() => handleEditClick(route)}
                            title="Edit route"
                          >
                            <Pencil size={16} />
                          </button>
                          <button
                            className="dispatch-history-delete-btn"
                            onClick={() => handleDeleteClick(route._id || route.id)}
                            title="Delete route"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="dispatch-history-pagination">
              <button
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={!pagination.hasPrev || loading}
                className="dispatch-history-pagination-button"
              >
                Previous
              </button>
              <span className="dispatch-history-pagination-info">
                Page {pagination.currentPage} of {pagination.totalPages}
              </span>
              <button
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={!pagination.hasNext || loading}
                className="dispatch-history-pagination-button"
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && selectedRoute && (
        <DispatchEditModal
          route={selectedRoute}
          isOpen={showEditModal}
          onClose={handleModalClose}
          onSave={handleSaveRoute}
          drivers={drivers}
          storeCoordinates={storeCoordinates}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteDialog && (
        <div className="dispatch-history-dialog-overlay">
          <div className="dispatch-history-dialog-box">
            <div className="dispatch-history-dialog-header">
              <h2>Confirm Delete</h2>
              <button className="dispatch-history-close-btn" onClick={cancelDelete}>
                <X size={20} />
              </button>
            </div>

            <div className="dispatch-history-delete-content">
              <p>Are you sure you want to delete this route history?</p>
              <p className="dispatch-history-delete-warning">This action cannot be undone.</p>
            </div>

            <div className="dispatch-history-dialog-actions">
              <button
                className="dispatch-history-cancel-btn"
                onClick={cancelDelete}
                disabled={deleteLoading}
              >
                Cancel
              </button>
              <button
                className="dispatch-history-confirm-delete-btn"
                onClick={confirmDelete}
                disabled={deleteLoading}
              >
                {deleteLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DispatchHistory;
